import React, { useState, useRef, useEffect, forwardRef, useId, useImperativeHandle, createContext } from "react";
import {
  FaEye,
  FaEyeSlash,
  FaSearch,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaCheckCircle,
} from "react-icons/fa";
import { CustomInputMask as InputMask, MaskSchema, ReplacementSchema } from "./InputMask";
import type { CustomInputMaskProps } from "./InputMask";
import { z } from "zod";
import { useInputMask } from "../../../hooks/useInputMask";
import isEqual from "react-fast-compare";
import Tooltip from "../Tooltip/Tooltip"; // Adicione este import
import clsx from "clsx"; // Você pode manter para lógica condicional de classes
// --- INÍCIO: Adicione o contexto de mensagens ---
// Corrija o import do Context
export interface InputFieldI18nContext {
  getDefaultErrorMessage: (type: string, error?: z.ZodError | null) => string;
  getDefaultSuccessMessage?: (type: string) => string;
  getRequiredErrorMessage?: (type: string) => string; // <-- Adicione esta linha
}
export const InputFieldMessagesContext = createContext<InputFieldI18nContext>({
  getDefaultErrorMessage: () => "Formato inválido",
  getRequiredErrorMessage: () => "Campo obrigatório", // <-- Default
});
// --- FIM: Adicione o contexto de mensagens ---

export interface InputFieldMessageContext {
  value: unknown;
  label?: string;
  error?: z.ZodError | null;
  inputType?: string;
}

export interface InputFieldPropsBase {
  id?: string;
  name?: string; // <-- Add this line
  placeholder?: string;
  label?: string;
  error?: boolean;
  errorMessage?: string | ((context: InputFieldMessageContext) => string);
  disabled?: boolean;
  helperText?: string;
  required?: boolean;
  theme?: "light" | "dark";
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onRightIconClick?: () => void;
  mask?: string | string[] | RegExp; // <-- Aceita RegExp agora
  replacement?: Record<string, RegExp>;
  validationRegex?: RegExp;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  accessibilityErrorMessage?: string;
  onFocus?: (() => void) | React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: (() => void) | React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onError?: (errorMessage: string) => void;
  autoComplete?: string; // <-- já existe
  autoFocus?: boolean;   // <-- já existe
  successMessage?: string | ((context: InputFieldMessageContext) => string); // Nova prop
  successIcon?: React.ReactNode; // Nova prop opcional para ícone
  readOnly?: boolean; // <-- ADICIONE ESTA LINHA
  className?: string; // <-- Adicione esta linha
  inputClassName?: string; // <-- já existe
  as?: "input" | "textarea" | "custom"; // <-- Adicione "custom" aqui
  maxLength?: number; // <-- Para textarea
  showCharCount?: boolean; // <-- Para textarea
  customValidation?: (value: unknown) => string | undefined; // <-- Adicione esta linha
  inputProps?: (React.InputHTMLAttributes<HTMLInputElement> & React.TextareaHTMLAttributes<HTMLTextAreaElement> & { "data-testid"?: string });
  tabIndex?: number; // <-- Adicione esta linha
  // --- INÍCIO: Adicione a prop para sobrescrever mensagens ---
  i18nMessages?: InputFieldI18nContext;
  // --- FIM ---
  containerProps?: React.HTMLAttributes<HTMLDivElement> & { "data-testid"?: string }; // <-- Corrija aqui
  tooltip?: string; // <-- Adicione esta linha
  tooltipPlacement?: "top" | "bottom" | "left" | "right"; // NOVO
  tooltipWidth?: number | string; // NOVO (ex: 200, "16rem")
  tooltipIconColor?: string; // NOVO (ex: "#0078d4")
  tooltipIconSize?: number | string; // NOVO (ex: 18, "1.25rem")
  children?: React.ReactNode; // <-- ADICIONE ESTA LINHA

  // Shorthand brasileiro para máscara/placeholder/validação
  cpf?: boolean;
  cnpj?: boolean;
  cpfCnpj?: boolean;
  cep?: boolean;
  money?: boolean;
  real?: boolean;
  dolar?: boolean;

  // 1. Acrescente as novas props na interface:
  borderColor?: string; // cor padrão da borda
  borderColorFocus?: string; // cor da borda ao focar
  borderColorError?: string; // cor da borda em erro
  borderColorSuccess?: string; // cor da borda em sucesso

  // Adicione as props de transformação de texto
  uppercase?: boolean;
  lowercase?: boolean;
  capitalize?: boolean;
  propercase?: boolean;
}

// Alias explícito para os tipos suportados (exceto "file")
export type InputFieldNonFileTypes =
  | "text"
  | "password"
  | "email"
  | "number"
  | "phone"
  | "tel"
  | "cep"
  | "date"
  | "time"
  | "datetime"
  | "datetime-local"
  | "money"
  | "search"
  | "cpf"
  | "url"
  | "color"
  | "month"
  | "week"
  | "hidden"
  | "range"
  | "checkbox"
  | "radio";

export interface InputFieldPropsFile extends InputFieldPropsBase {
  type: "file";
  value: File | File[] | null;
  onChange: (value: File | File[] | null) => void;
  multiple?: boolean;
  accept?: string;
}

export interface InputFieldPropsDefault<T = string> extends InputFieldPropsBase {
  // Substitui o Exclude anterior por alias explícito para evitar widening
  type?: InputFieldNonFileTypes;
  value: T;
  onChange: (value: T) => void;
}

// Discriminated union
export type InputFieldProps<T = string> = InputFieldPropsFile | InputFieldPropsDefault<T>;

/**
 * InputField - Componente de campo de entrada reutilizável, acessível e altamente configurável.
 *
 * Suporta múltiplos tipos de input, máscaras, validação (incluindo customValidation), ícones, mensagens de erro e sucesso, acessibilidade, integração com React Hook Form e internacionalização de mensagens.
 * 
 * Principais recursos:
 * - Suporte a input, textarea e file.
 * - Máscaras de entrada (string, array ou RegExp) e validação customizada.
 * - Ícones à esquerda e à direita, incluindo botão de visibilidade para senha.
 * - Tooltip acessível no label (ícone "i"), com suporte a mouse, foco e teclado.
 * - Mensagens de erro, sucesso e texto auxiliar.
 * - Totalmente acessível: roles, aria-labels, aria-describedby, aria-live, aria-expanded, aria-controls, navegação por teclado.
 * - Suporte a temas claro e escuro.
 * - Internacionalização de mensagens via contexto ou prop.
 * - Compatível com React Hook Form.
 * - Testável via data-testid customizável.
 * - Suporte a tabIndex tanto no container quanto no input.
 * - Suporte a children para customização avançada (as="custom").
 *
 * @template T Tipo do valor do input (string, number, Date, File, File[], etc).
 * @param {InputFieldProps<T>} props Propriedades do componente.
 * @param {React.Ref<HTMLInputElement | HTMLTextAreaElement>} ref Referência para o input ou textarea, conforme o prop `as`.
 * @returns {JSX.Element} Elemento JSX do campo de entrada.
 *
 * @example
 * <InputField
 *   type="email"
 *   label="E-mail"
 *   value={email}
 *   onChange={setEmail}
 *   error={!!emailError}
 *   errorMessage={emailError}
 *   required
 *   leftIcon={<FaEnvelope />}
 *   autoComplete="email"
 *   autoFocus
 *   tooltip="Seu e-mail institucional"
 * />
 *
 * @example
 * // Para input de arquivo:
 * <InputField
 *   type="file"
 *   value={selectedFiles}
 *   onChange={setSelectedFiles}
 *   multiple
 *   accept="image/*"
 *   autoFocus
 * />
 *
 * @see InputFieldProps para todas as propriedades disponíveis.
 *
 * @remarks
 * - Para melhor performance, funções passadas como props (ex: `onChange`, `onFocus`, `onBlur`, `onError`, `customValidation`, etc.)
 *   devem ser estáveis entre renders. Use sempre `useCallback` ao declará-las no componente pai.
 * - O atributo `data-testid` do container segue a seguinte prioridade:
 *   1. `containerProps["data-testid"]`
 *   2. `inputProps["data-testid"]`
 *   3. Valor padrão: `"input-field"`
 * - Tooltip: O ícone "i" só aparece se a prop `tooltip` for fornecida. O tooltip é acessível por mouse, foco e teclado (Enter/Espaço/Escape).
 * - Acessibilidade: O componente utiliza roles e atributos ARIA para garantir navegação e leitura por leitores de tela.
 * - Quando `type="file"`, o valor e o onChange devem ser do tipo `File | File[] | null` (nunca string).
 * - Para textarea, use `as="textarea"` e garanta que o value seja string.
 * - Internacionalização: Mensagens padrão podem ser sobrescritas via contexto ou prop `i18nMessages`.
 * - Suporte a máscara de entrada via prop `mask` (string, array ou RegExp) e `replacement`.
 * - Suporte a validação customizada via prop `customValidation`.
 * - Suporte a contagem de caracteres em textarea via `showCharCount` e `maxLength`.
 * - Suporte a temas claro e escuro via prop `theme`.
 * - Suporte a ícones customizados à esquerda (`leftIcon`) e à direita (`rightIcon`).
 * - Suporte a mensagens de sucesso via `successMessage` e `successIcon`.
 * - Suporte a propriedades extras para o input via `inputProps` e para o container via `containerProps`.
 * - Suporte a children para customização avançada quando `as="custom"`.
 * - Suporte a tabIndex tanto no container quanto no input.
 */
// Sobrecarga para outros tipos
// Implementação real abaixo
const InputField = React.memo(
  forwardRef<InputFieldRef, InputFieldProps<unknown>>(
    (props, ref) => {
      /**
       * Propriedades do componente InputField.
       *
       * @typedef {Object} InputFieldProps
       * @property {"text"|"password"|"email"|"number"|"phone"|"cep"|"date"|"time"|"datetime"|"money"|"search"|"cpf"|"file"} [type="text"] Tipo do input.
       * @property {T} value Valor do input.
       * @property {(value: T) => void} onChange Função chamada ao alterar o valor.
       * @property {string} [placeholder] Placeholder do input.
       * @property {string} [label] Rótulo exibido acima do input.
       * @property {boolean} [error] Indica se há erro de validação.
       * @property {string} [errorMessage] Mensagem de erro exibida.
       * @property {boolean} [disabled] Desabilita o input.
       * @property {string} [helperText] Texto auxiliar exibido abaixo do input.
       * @property {boolean} [required] Indica se o campo é obrigatório.
       * @property {"light"|"dark"} [theme="light"] Tema visual.
       * @property {React.ReactNode} [leftIcon] Ícone à esquerda do input.
       * @property {React.ReactNode} [rightIcon] Ícone à direita do input.
       * @property {() => void} [onRightIconClick] Callback ao clicar no ícone da direita.
       * @property {string} [mask] Máscara de entrada.
       * @property {Record<string, RegExp>} [replacement] Regras de substituição para máscara.
       * @property {RegExp} [validationRegex] Regex de validação customizada.
       * @property {string} [accessibilityLabel] Label para acessibilidade.
       * @property {string} [accessibilityHint] Dica para acessibilidade.
       * @property {string} [accessibilityErrorMessage] Mensagem de erro para acessibilidade.
       * @property {() => void} [onFocus] Callback ao focar o input.
       * @property {() => void} [onBlur] Callback ao desfocar o input.
       * @property {(errorMessage: string) => void} [onError] Callback ao ocorrer erro de validação.
       * @property {boolean} [multiple] Permite seleção múltla (apenas para type="file").
       * @property {string} [accept] Tipos de arquivos aceitos (apenas para type="file").
       * @property {boolean} [useDateMask] Usa máscara para datas.
       * @property {string} [autoComplete] Sugestão de preenchimento automático.
       * @property {string} [successMessage] Mensagem de sucesso.
       * @property {React.ReactNode} [successIcon] Ícone de sucesso.
       */
      const {
        id,
        name, // <-- Add this line
        type = "text",
        value,
        onChange, // <-- use esta variável!
        placeholder = "",
        label,
        error = false,
        errorMessage,
        disabled = false,
        helperText,
        required = false,
        theme = "light",
        leftIcon,
        rightIcon,
        onRightIconClick,
        mask, // <-- já existe
        replacement, // Used in mask logic below
        validationRegex,
        accessibilityLabel,
        accessibilityErrorMessage,
        onFocus,
        onBlur,
        onError,
        autoComplete, 
        autoFocus, // <-- já existe
        readOnly, // <-- ADICIONE ESTA LINHA
        className, // <-- Adicione aqui
        inputClassName, // <-- já existe
        as = "input", // <-- Adicione aqui
        maxLength, // <-- Adicione aqui
        showCharCount, // <-- Adicione aqui
        inputProps = {}, // <-- Fallback para evitar undefined
        tabIndex, // <-- Adicione aqui
        // --- INÍCIO: Use o contexto ou prop para mensagens ---
        i18nMessages,
        // --- FIM ---
        containerProps, // <-- Adicione esta linha
        tooltip, // <-- Adicione esta linha
        tooltipPlacement, // NOVO
        tooltipWidth, // NOVO
        tooltipIconColor, // NOVO
        tooltipIconSize, // NOVO
        children, // <-- ADICIONE ESTA LINHA
        // Adicione as props de shorthand brasileiro
        cpf,
        cnpj,
        cpfCnpj,
        cep,
        money,
        real,
        dolar,
        // 1. Acrescente as props na interface:
        uppercase,
        lowercase,
        capitalize,
        propercase,
        // 2. Desestruture as props no componente:
        borderColor,
        borderColorFocus,
        borderColorError,
        borderColorSuccess,
      } = props;

      // Gere o id apenas se não for passado via props
      const generatedId = useId();
      const inputId = inputProps.id || id || generatedId;

      const [isFocused, setIsFocused] = useState(false);
      const [isPasswordVisible, setIsPasswordVisible] = useState(false);
      const [hasError, setHasError] = useState(() => !!error);
      const [showSuccess, setShowSuccess] = useState(false);
      const [zodError, setZodError] = useState<z.ZodError | null>(null);
      const successHideTimeout = useRef<NodeJS.Timeout | null>(null);
      const wasErrorRef = useRef(false);

      // Limpe o timeout ao desmontar
      useEffect(() => {
        return () => {
          if (successHideTimeout.current) {
            clearTimeout(successHideTimeout.current);
          }
        };
      }, []);

      useEffect(() => {
        setHasError(error);
        if (error) {
          wasErrorRef.current = true;
          setShowSuccess(false);
        }
      }, [error]);

      // Rode validação sempre que o valor mudar (inclusive no mount)
      useEffect(() => {
        handleValidation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [value]);

      type ShorthandType = "cpf" | "cnpj" | "cpfCnpj" | "cep" | "real" | "dolar";
let shorthandType: ShorthandType | undefined;
if (cpfCnpj) shorthandType = "cpfCnpj";
else if (cpf) shorthandType = "cpf";
else if (cnpj) shorthandType = "cnpj";
else if (cep) shorthandType = "cep";
else if (real || money) shorthandType = "real";
else if (dolar) shorthandType = "dolar";

const shorthandConfig: Record<ShorthandType, {
  mask?: string | string[];
  placeholder?: string;
  validationRegex?: RegExp;
  inputMode?: string;
}> = {
  cpf: {
    mask: "999.999.999-99",
    placeholder: "000.000.000-00",
    validationRegex: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
    inputMode: "numeric",
  },
  cnpj: {
    mask: "99.999.999/9999-99",
    placeholder: "00.000.000/0000-00",
    validationRegex: /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/,
    inputMode: "numeric",
  },
  cpfCnpj: {
    mask: ["999.999.999-99", "99.999.999/9999-99"],
    placeholder: "CPF ou CNPJ",
    validationRegex: /^(\d{3}\.\d{3}\.\d{3}-\d{2}|\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})$/,
    inputMode: "numeric",
  },
  cep: {
    mask: "99999-999",
    placeholder: "00000-000",
    validationRegex: /^\d{5}-\d{3}$/,
    inputMode: "numeric",
  },
  real: {
    mask: undefined,
    placeholder: "R$ 0,00",
    validationRegex: /^\d+([.,]\d{2})?$/,
    inputMode: "decimal",
  },
  dolar: {
    mask: undefined,
    placeholder: "US$ 0.00",
    validationRegex: /^\d+(\.\d{2})?$/,
    inputMode: "decimal",
  },
};

const shorthand = shorthandType ? shorthandConfig[shorthandType] : undefined;

// Só sobrescreve se o usuário NÃO passou manualmente
const effectiveMask = shorthand?.mask ?? mask;
const effectivePlaceholder = shorthand?.placeholder ?? placeholder;
const effectiveValidationRegex = shorthand?.validationRegex ?? validationRegex;
const effectiveInputMode = shorthand?.inputMode ?? (inputProps?.inputMode as string | undefined);
// --- FIM: Shorthand brasileiro ---



      // Memoize getSchemaByType to avoid changing dependencies on every render
      const getSchemaByType = React.useCallback(
        (type: string, _validationRegex?: RegExp, mask?: string | string[] | RegExp) => {
          // --- PATCH: Accept whitespace-only as empty for required fields ---
          const whitespaceRefine = (val: unknown) =>
            typeof val === "string" ? val.trim().length > 0 : false;

          // PATCH: If mask is RegExp or array, use it as validation
          if (
            mask &&
            (Object.prototype.toString.call(mask) === "[object RegExp]" ||
              Array.isArray(mask))
          ) {
            let regex: RegExp | undefined;
            if (Object.prototype.toString.call(mask) === "[object RegExp]") {
              regex = mask as RegExp;
            } else if (Array.isArray(mask)) {
              // Convert mask array to regex: e.g. ["9","9","9"] => /^\d{3}$/
              const maskStr = mask.join("");
              // Only supports digit masks for now
              if (/^9+$/.test(maskStr)) {
                regex = new RegExp(`^\\d{${maskStr.length}}$`);
              }
            }
            if (regex) {
              return z
                .string()
                .refine(whitespaceRefine, "Campo obrigatório") // <-- FIRST!
                .refine(
                  val =>
                    typeof val === "string" && val.trim().length > 0
                      ? regex!.test(val)
                      : true, // Só valida formato se não for vazio/whitespace
                  "Formato inválido"
                );
            }
          }

          switch (type) {
            case "cep":
              return z
                .string()
                .refine(whitespaceRefine, "Campo obrigatório")
                .refine(
                  val =>
                    typeof val === "string" && val.trim().length > 0
                      ? /^\d{5}-\d{3}$/.test(val)
                      : true,
                  "CEP inválido"
                );
            case "cpf":
              return z
                .string()
                .refine(whitespaceRefine, "Campo obrigatório")
                .refine(
                  (cpf) => {
                    if (typeof cpf !== "string" || cpf.trim().length === 0) return true;
                    const cleanCpf = cpf.replace(/\D/g, "");
                    if (cleanCpf.length !== 11 || /^(\d)\1+$/.test(cleanCpf))
                      return false;
                    let sum = 0;
                    for (let i = 0; i < 9; i++)
                      sum += parseInt(cleanCpf[i]) * (10 - i);
                    let firstVerifier = (sum * 10) % 11;
                    if (firstVerifier === 10) firstVerifier = 0;
                    if (firstVerifier !== parseInt(cleanCpf[9])) return false;
                    sum = 0;
                    for (let i = 0; i < 10; i++)
                      sum += parseInt(cleanCpf[i]) * (11 - i);
                    let secondVerifier = (sum * 10) % 11;
                    if (secondVerifier === 10) secondVerifier = 0;
                    return secondVerifier === parseInt(cleanCpf[10]);
                  },
                  { message: "CPF inválido" }
                );
            case "email":
              return z
                .string()
                .refine(whitespaceRefine, "Campo obrigatório")
                .refine(
                  val =>
                    typeof val === "string" && val.trim().length > 0
                      ? z.string().email().safeParse(val).success
                      : true,
                  "Email inválido"
                );
            case "phone":
              return z
                .string()
                .refine(whitespaceRefine, "Campo obrigatório")
                .refine(
                  val =>
                    typeof val === "string" && val.trim().length > 0
                      ? /^\(\d{2}\) \d{5}-\d{4}$/.test(val)
                      : true,
                  "Telefone inválido"
                );
            case "date":
              return z
                .string()
                .refine(whitespaceRefine, "Campo obrigatório")
                .refine(
                  val =>
                    typeof val === "string" && val.trim().length > 0
                      ? /^\d{4}-\d{2}-\d{2}$/.test(val)
                      : true,
                  "Data inválida"
                );
            case "url":
              return z
                .string()
                .refine(whitespaceRefine, "Campo obrigatório")
                .refine(
                  val => (typeof val === "string" && val.trim().length > 0 ? /^https?:\/\/.+/i.test(val) : true),
                  "URL inválida"
                );
            case "color":
              return z
                .string()
                .refine(whitespaceRefine, "Campo obrigatório")
                .refine(
                  val => (typeof val === "string" && val.trim().length > 0 ? /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(val) : true),
                  "Cor inválida"
                );
            case "datetime-local":
              return z
                .string()
                .refine(whitespaceRefine, "Campo obrigatório")
                .refine(
                  val =>
                    (typeof val === "string" && val.trim().length > 0
                      ? /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(val)
                      : true),
                  "Data/hora inválida"
                );
            // month (YYYY-MM) e week (YYYY-Www) validação simples
            case "month":
              return z
                .string()
                .refine(whitespaceRefine, "Campo obrigatório")
                .refine(
                  val => (typeof val === "string" && val.trim().length > 0 ? /^\d{4}-\d{2}$/.test(val) : true),
                  "Mês inválido"
                );
            case "week":
              return z
                .string()
                .refine(whitespaceRefine, "Campo obrigatório")
                .refine(
                  val => (typeof val === "string" && val.trim().length > 0 ? /^\d{4}-W\d{2}$/.test(val) : true),
                  "Semana inválida"
                );
            // checkbox/radio normalmente não validam formato aqui; apenas obrigatório
            case "checkbox":
            case "radio":
              return z.string().refine(whitespaceRefine, "Campo obrigatório");
            default:
              if (validationRegex) {
                return z
                  .string()
                  .refine(whitespaceRefine, "Campo obrigatório")
                  .refine(
                    val =>
                      typeof val === "string" && val.trim().length > 0
                        ? validationRegex.test(val)
                        : true,
                    "Formato inválido"
                  );
              }
              return z
                .string()
                .refine(whitespaceRefine, "Campo obrigatório");
          }
        },
        [validationRegex]
      );

      // Desestrute customValidation antes do useCallback
      const handleValidation = React.useCallback(() => {
        const { customValidation, mask } = props;

        let normalizedValue = value;
        if (typeof value === "string" && value.trim().length === 0) {
          normalizedValue = "";
        }
        if (value === null || value === undefined) {
          normalizedValue = "";
        }

        // Validação de máscara via InputMask (se houver)
        if (
          mask &&
          (
            typeof mask === "string" ||
            Array.isArray(mask) ||
            mask instanceof RegExp
          )
        ) {
          // Valida máscara usando MaskSchema do InputMask
          const maskParse = MaskSchema.safeParse(mask);
          if (maskParse.success) {
            let maskValid = true;
            let maskErrorMsg: string | undefined;
            if (mask instanceof RegExp) {
              maskValid = mask.test(String(normalizedValue));
              if (!maskValid) maskErrorMsg = "Valor não corresponde ao padrão da máscara.";
            } else if (typeof mask === "string" || Array.isArray(mask)) {
              // Para máscara string/array, valida se todos os caracteres obrigatórios estão presentes
              const rawValue = String(normalizedValue ?? "");
              const maskStr = Array.isArray(mask) ? mask[0] : mask;
              if (maskStr && rawValue.length > 0) {
                // Conta quantos caracteres válidos existem
                const expectedLen = maskStr.replace(/[^AaBb9*]/g, "").length;
                const actualLen = rawValue.replace(/[^A-Za-z0-9]/g, "").length;
                if (actualLen < expectedLen) {
                  maskValid = false;
                  maskErrorMsg = "Valor incompleto para a máscara.";
                }
              }
            }
            if (!maskValid) {
              setHasError(true);
              setShowSuccess(false);
              wasErrorRef.current = true;
              setZodError(
                new z.ZodError([
                  {
                    code: "custom",
                    message: maskErrorMsg || "Valor inválido para a máscara.",
                    path: [],
                  },
                ])
              );
              if (onError) onError(maskErrorMsg || "Valor inválido para a máscara.");
              return;
            }
          }
        }

        // Se customValidation foi fornecida, use-a
        if (typeof customValidation === "function") {
          const customError = customValidation(normalizedValue);
          if (customError) {
            setHasError(true);
            setShowSuccess(false);
            wasErrorRef.current = true;
            setZodError(null);
            if (onError) onError(customError);
          } else {
            setHasError(false);
            setZodError(null);
            if (wasErrorRef.current) {
              setShowSuccess(true);
              wasErrorRef.current = false;
            } else {
              setShowSuccess(false);
            }
          }
          return;
        }

        // Se required e vazio, não rode Zod, só seta erro obrigatório
        if (required && isEmpty(normalizedValue)) {
          setHasError(true);
          setShowSuccess(false);
          wasErrorRef.current = true;
          setZodError(
            new z.ZodError([
              {
                code: "custom",
                message: "Campo obrigatório",
                path: [],
              },
            ])
          );
          if (onError) onError("Campo obrigatório");
          return;
        }

        // Fallback para Zod
        try {
          const schema = getSchemaByType(type, effectiveValidationRegex, effectiveMask);

          // --- CORREÇÃO: converte Date para string antes de validar ---
          let valueToValidate = normalizedValue;
          if (
            (type === "date" || type === "datetime" || type === "time") &&
            value instanceof Date
          ) {
            if (type === "date") {
              valueToValidate = value.toISOString().slice(0, 10);
            } else if (type === "datetime") {
              valueToValidate = value.toISOString().slice(0, 16);
            } else if (type === "time") {
              valueToValidate = value.toISOString().slice(11, 16);
            }
          }
          // PATCH: Always validate as string for masked fields
          if (
            mask &&
            (Object.prototype.toString.call(mask) === "[object RegExp]" ||
              Array.isArray(mask))
          ) {
            valueToValidate = typeof normalizedValue === "string" ? normalizedValue : String(normalizedValue ?? "");
          }

          schema.parse(valueToValidate);
          setHasError(false);
          setZodError(null);
          // PATCH: Sempre mostrar sucesso se não houver erro e houver successMessage ou successIcon
          if (props.successMessage || props.successIcon) {
            setShowSuccess(true);
            wasErrorRef.current = false;
          } else {
            setShowSuccess(false);
          }
        } catch (err) {
          setHasError(true);
          setShowSuccess(false);
          wasErrorRef.current = true;
          if (err instanceof z.ZodError) {
            setZodError(err);
            if (onError) {
              onError(err.issues[0]?.message || "Formato inválido");
            }
          } else {
            setZodError(null);
          }
        }
      }, [props, value, onError, type, getSchemaByType, required, effectiveMask, effectiveValidationRegex]);

      const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (typeof onChange !== "function") return;

        if (type === "file") {
          const { multiple, accept } = props as InputFieldPropsFile;
          const files = e.target.files ? Array.from(e.target.files) : [];

          // --- INÍCIO: Checagem de tipos de arquivo ---
          let acceptList: string[] = [];
          if (accept) {
            acceptList = accept
              .split(",")
              .map((a) => a.trim())
              .filter(Boolean);
          }

          const isAccepted = (file: File) => {
            if (acceptList.length === 0) return true;
            return acceptList.some((acceptType) => {
              if (acceptType.startsWith(".")) {
                // Extensão
                return file.name.toLowerCase().endsWith(acceptType.toLowerCase());
              }
              if (acceptType.endsWith("/*")) {
                // image/*, audio/* etc
                const baseType = acceptType.replace("/*", "");
                return file.type.startsWith(baseType);
              }
              // MIME type exato
              return file.type === acceptType;
            });
          };

          const filteredFiles = files.filter(isAccepted);

          if (filteredFiles.length === 0 && files.length === 0) {
            // Não chama onChange se não há arquivos
            return;
          }
          (onChange as (value: File | File[] | null) => void)(
            multiple ? (filteredFiles.length > 0 ? filteredFiles : null) : filteredFiles[0] ?? null
          );
          return;
        }

        if (type === "number") {
          const val = e.target.value;
          // Só chama onChange se for number | ""
          (onChange as (value: number | "") => void)(val === "" ? "" : Number(val));
          return;
        }

        if (type === "date" || type === "datetime" || type === "datetime-local" || type === "time") {
          const val = e.target.value;
          (onChange as (value: Date | "") => void)(val ? new Date(val) : "");
          return;
        }

        // Default: string
        let val = e.target.value;
        val = transformText(val, { uppercase, lowercase, capitalize, propercase });
        (onChange as (value: string) => void)(val);

        // Remova este setHasError(false); para não sobrescrever erro antes da validação
        // setHasError(false);

        if (showSuccess) {
          if (successHideTimeout.current) clearTimeout(successHideTimeout.current);
          successHideTimeout.current = setTimeout(() => setShowSuccess(false), 400);
        }
        wasErrorRef.current = hasError;

        // Rode validação em microtask para garantir que value já foi atualizado
        Promise.resolve().then(() => handleValidation());
      };

      const getLeftIcon = () => {
        if (leftIcon) return leftIcon;

        switch (type) {
          case "search":
            return <FaSearch />;
          case "date":
            return null;
          case "email":
            return <FaEnvelope />;
          case "phone":
            return <FaPhone />;
          case "password":
            return <FaLock />;
          case "datetime":
            return null; // Não exibe ícone à esquerda para datetime
          default:
            return null;
        }
      };

      // Only pass mask as string to useInputMask, ignore RegExp or string[] for hook
      const maskString = typeof mask === "string" ? mask : undefined;
      const maskProps = useInputMask(type, maskString, replacement) as Partial<CustomInputMaskProps>;


      // Memoize getHtmlInputType
      const getHtmlInputType = React.useCallback(() => {
        // Se houver máscara (maskProps) mantém como text
        if (maskProps) return "text";
        if (type === "date") return "date";
        if (type === "datetime" || type === "datetime-local") return "datetime-local";
        if (type === "time") return "time";
        if (type === "phone" || type === "tel") return "tel";
        if (["url","color","month","week","hidden","range","search","email","number","password"].includes(type)) return type;
        if (type === "checkbox") return "checkbox";
        if (type === "radio") return "radio";
        return "text";
      }, [type, maskProps]);

      // Memoize getInputValue
      const getInputValue = React.useCallback(() => {
        if (type === "date") {
          if (value instanceof Date) return value.toISOString().slice(0, 10);
          if (typeof value === "string") {
            const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
            return match ? match[1] : value;
          }
          return "";
        }
        if (type === "datetime" || type === "datetime-local") {
          if (value instanceof Date) {
            return value.toISOString().slice(0, 16);
          }
          if (typeof value === "string") {
            const match = value.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
            return match ? match[1] : value;
          }
          return "";
        }
        if (type === "time") {
          if (value instanceof Date) {
            // time espera "HH:MM"
            return value.toISOString().slice(11, 16);
          }
          if (typeof value === "string") {
            // Se for string ISO, converta para HH:mm
            const match = value.match(/T(\d{2}:\d{2})/);
            return match ? match[1] : value;
          }
          return "";
        }
        if (typeof value === "string") return value;
        if (typeof value === "number") return String(value);
        if (Array.isArray(value)) return value.join(", ");
        return "";
      }, [type, value]);

      // --- INÍCIO: Atualize resolveMessage para usar i18n ---
      // Obtenha o contexto de mensagens ou use a prop i18nMessages
      const contextI18n = React.useContext(InputFieldMessagesContext);
      const i18n = i18nMessages || contextI18n;

      // Nova função para priorizar mensagens de erro específicas
      const getErrorText = (
        errorMessage: string | ((context: InputFieldMessageContext) => string) | undefined,
        accessibilityErrorMessage: string | undefined,
        zodError: z.ZodError | null,
        type: string,
        value: unknown
      ) => {
        // Normalize value for empty check
        let normalizedValue = value;
        if (typeof value === "string" && value.trim().length === 0) {
          normalizedValue = "";
        }
        if (value === null || value === undefined) {
          normalizedValue = "";
        }

        // 1. Mensagem customizada (errorMessage prop, function or string)
        if (typeof errorMessage === "function") {
          try {
            const msg = errorMessage({ value, label, error: zodError, inputType: type });
            if (msg) return msg;
          } catch {
            return "Formato inválido";
          }
        } else if (typeof errorMessage === "string" && errorMessage.trim() !== "") {
          return errorMessage;
        }
        // 2. accessibilityErrorMessage (if present and error)
        if (accessibilityErrorMessage && accessibilityErrorMessage.trim() !== "") {
          return accessibilityErrorMessage;
        }

        // 3. Mensagem do Zod (CPF, CEP, etc) - sempre priorize se existir
        if (zodError?.issues?.length) {
          const issue = zodError.issues[0];
          // Se for erro de obrigatório, use getRequiredErrorMessage
          if (issue.message === "Campo obrigatório" && i18n?.getRequiredErrorMessage) {
            return i18n.getRequiredErrorMessage(type) || "Campo obrigatório";
          }
          // Sempre priorize a mensagem do Zod, se não for obrigatório
          if (issue.message && issue.message !== "Campo obrigatório") {
            return issue.message;
          }
          // Fallback para i18n
          if (i18n?.getDefaultErrorMessage) {
            return i18n.getDefaultErrorMessage(type, zodError);
          }
          // Último fallback
          if (issue.message) return issue.message;
        }

        // 4. Campo obrigatório: se required e vazio/whitespace, retorna i18n.getDefaultErrorMessage (ou "Campo obrigatório")
        if (required && isEmpty(normalizedValue)) {
          if (i18n && typeof i18n.getRequiredErrorMessage === "function") {
            return i18n.getRequiredErrorMessage(type) || "Campo obrigatório";
          }
          return "Campo obrigatório";
        }

        // 5. Se validationRegex existe e value não passa, mostre errorMessage se existir
        if (
          !!validationRegex &&
          typeof normalizedValue === "string" &&
          normalizedValue.length > 0 &&
          !validationRegex.test(normalizedValue) &&
          typeof errorMessage === "string" &&
          errorMessage.trim() !== ""
        ) {
          return errorMessage;
        }

        // 6. i18n fallback
        if (i18n && typeof i18n.getDefaultErrorMessage === "function") {
          return i18n.getDefaultErrorMessage(type, zodError);
        }
        return "Formato inválido";
      };

      const resolveMessage = React.useCallback(
        (
          msg: string | ((context: InputFieldMessageContext) => string) | undefined,
          value: unknown,
          error?: z.ZodError | null
        ) => {
          // PATCH: Defensive: nunca crasha se msg lança
          if (typeof msg === "function" || typeof msg === "string") {
            return safeRenderMessage(
              msg,
              { value, label, error, inputType: type }
            );
          }
          // Use i18n para mensagem padrão
          if (hasError) return i18n.getDefaultErrorMessage(type, error);
          if (showSuccess && i18n.getDefaultSuccessMessage)
            return i18n.getDefaultSuccessMessage(type);
          return undefined;
        },
        [label, type, hasError, showSuccess, i18n]
      );
      // --- FIM ---

      // Memoize getDescribedById
      const getDescribedById = React.useCallback(() => {
        if (hasError) return "error-message";
        if (showSuccess && (props.successMessage || props.successIcon)) return "success-message";
        if (helperText) return "helper-text";
        return undefined;
      }, [hasError, showSuccess, props.successMessage, props.successIcon, helperText]);

      // Função para alternar visibilidade da senha
      const handlePasswordIconClick = React.useCallback(() => {
        setIsPasswordVisible((v) => !v);
      }, []);

      // Função para tratar tecla no ícone de senha
      const handlePasswordIconKeyDown = React.useCallback(
        (e: React.KeyboardEvent<HTMLSpanElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            setIsPasswordVisible((v) => !v);
          }
        },
        []
      );

      // PATCH: Controla exibição do texto de erro apenas quando deve aparecer (após blur/validação)
      // Novo: só mostra erro obrigatório após blur
      const [wasBlurred, setWasBlurred] = useState(false);

      const handleInputBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setIsFocused(false);
        setWasBlurred(true); // <-- Marca que houve blur
        if (onBlur) {
          (onBlur as React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>)(e);
        }
        handleValidation();
      }, [onBlur, handleValidation]);

      // PATCH: Não mostra erro obrigatório até blur
      useEffect(() => {
        if (!wasBlurred && hasError && zodError?.issues?.[0]?.message === "Campo obrigatório") {
          setHasError(false);
        }
      }, [wasBlurred, hasError, zodError]);

      // PATCH: Evita warning de textarea undefined
      useEffect(() => {
        if (
          process.env.NODE_ENV !== "production" &&
          as === "textarea" &&
          (value === undefined || value === null)
        ) {
          // Não faz nada, não loga warning para undefined/null
          return;
        }
        if (
          process.env.NODE_ENV !== "production" &&
          as === "textarea" &&
          typeof value !== "string"
        ) {
          // eslint-disable-next-line no-console
          console.warn(
            `[InputField] O value deve ser string quando as="textarea". Recebido:`,
            value
          );
        }
      }, [as, value]);

      // Ref interno para o elemento real (input ou InputMask)
      const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

      // Exponha o elemento real para o ref externo
      useImperativeHandle(ref, () => inputRef.current!);

      // Aviso de dev se value não for string quando as="textarea"
      useEffect(() => {
        if (process.env.NODE_ENV !== "production" && as === "textarea" && typeof value !== "string") {
          // eslint-disable-next-line no-console
          console.warn(
            `[InputField] O value deve ser string quando as="textarea". Recebido:`,
            value
          );
        }
      }, [as, value]);

      // Desestrute ANTES do useEffect:
      const { onChange: onChangeProp, onFocus: onFocusProp, onError: onErrorProp, customValidation: customValidationProp } = props;

      useEffect(() => {
        if (process.env.NODE_ENV !== "production") {
          const fnProps = [
            ["onChange", onChangeProp],
            // ["onBlur", onBlurProp],  // <-- removido para evitar warning ruidoso (handlers recriados pelo container)
            ["onFocus", onFocusProp],
            ["onError", onErrorProp],
            ["customValidation", customValidationProp],
          ] as const;
          fnProps.forEach(([key, fn]) => {
            const refKey = `__last_${key}`;
            // @ts-expect-error internal ref bag
            if (!inputRef.current) inputRef.current = {};
            // @ts-expect-error store previous
            if (typeof fn === "function" && inputRef.current[refKey] && inputRef.current[refKey] !== fn) {
              console.warn(
                `[InputField] A prop ${key} mudou entre renders. Use useCallback para evitar renders desnecessários.`
              );
            }
            // @ts-expect-error assign current
            if (typeof fn === "function") inputRef.current[refKey] = fn;
          });
        }
      }, [onChangeProp, onFocusProp, onErrorProp, customValidationProp]);

      // (Removido: função getDefaultMask não utilizada)

      // Adicione ANTES do renderInput:
      const handleTextareaChange = React.useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
          if (typeof onChange === "function" && type !== "file") {
            let val = e.target.value;
            val = transformText(val, { uppercase, lowercase, capitalize, propercase });
            (onChange as (value: string) => void)(val);
          }
          if (showSuccess) {
            if (successHideTimeout.current) clearTimeout(successHideTimeout.current);
            successHideTimeout.current = setTimeout(() => setShowSuccess(false), 400);
          }
          wasErrorRef.current = hasError;
          Promise.resolve().then(() => handleValidation());
        },
        [onChange, type, showSuccess, hasError, handleValidation, uppercase, lowercase, capitalize, propercase]
      );

      // Move handleInputFocus to function body root
      const handleInputFocus = React.useCallback(
        (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          setIsFocused(true);
          if (onFocus) {
            (onFocus as React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>)(event);
          }
        },
        [onFocus]
      );

      const renderInput = () => {
        if (as === "custom") {
          const hasLeftIcon = !!getLeftIcon();
          const wrapperClass = clsx(
            "relative flex items-center w-full px-3 py-2 rounded-md transition-colors duration-150",
            theme === "dark"
              ? "bg-[#23242a] text-[#e3eaff] border var(--if-border-color, #33384d) focus-within:border var(--if-border-color-focus, #7b5cff) focus-within:ring-2 focus-within:ring-[#7b5cff] focus-within:bg-[#232c3a]"
              : "bg-white text-gray-900 border var(--if-border-color, #c8c6c4) focus-within:border var(--if-border-color-focus, #0078d4) focus-within:ring-2 focus-within:ring-[#c7e0f4] focus-within:bg-[#f3f9fd]",
            "font-spaceGrotesk",
            isFocused && !hasError && !showSuccess && (theme === "dark"
              ? "border var(--if-border-color-focus, #7b5cff) ring-2 ring-[#7b5cff] bg-[#232c3a]"
              : "border var(--if-border-color-focus, #0078d4) ring-2 ring-[#c7e0f4] bg-[#f3f9fd]"),
            hasError && (theme === "dark"
              ? "border var(--if-border-color-error, #ff7675) bg-[#2a232a] ring-2 ring-[#ffb6b6]"
              : "border var(--if-border-color-error, #d13438) bg-[#fdf6f6] ring-2 ring-[#f3b6b6]"),
            showSuccess && (theme === "dark"
              ? "border var(--if-border-color-success, #00e676) bg-[#1a2a1a] ring-2 ring-[#00e676]"
              : "border var(--if-border-color-success, #107c10) bg-[#f6fff6] ring-2 ring-[#b7f3b6]"),
            disabled && (theme === "dark"
              ? "bg-[#181c24] text-[#666] cursor-not-allowed"
              : "bg-[#f3f2f1] text-[#a19f9d] cursor-not-allowed"),
            inputClassName
          );
          return (
            <div className={wrapperClass}>
              {hasLeftIcon && (
                <span className="icon left-icon mr-2 text-[#605e5c]">{getLeftIcon()}</span>
              )}
              <div className="flex-1 min-w-0">{children}</div>
              {/* Se quiser, pode adicionar rightIcon aqui também */}
            </div>
          );
        }

        const hasLeftIcon = !!getLeftIcon();

        // Wrapper: borda, bg, radius, transições, padding!
        const wrapperClass = clsx(
          "relative flex items-center w-full px-3 py-2 rounded-md transition-colors duration-150",
          theme === "dark"
            ? "bg-[#23242a] text-[#e3eaff] border var(--if-border-color, #33384d) focus-within:border var(--if-border-color-focus, #7b5cff) focus-within:ring-2 focus-within:ring-[#7b5cff] focus-within:bg-[#232c3a]"
            : "bg-white text-gray-900 border var(--if-border-color, #c8c6c4) focus-within:border var(--if-border-color-focus, #0078d4) focus-within:ring-2 focus-within:ring-[#c7e0f4] focus-within:bg-[#f3f9fd]",
          "font-spaceGrotesk",
          isFocused && !hasError && !showSuccess && (theme === "dark"
            ? "border var(--if-border-color-focus, #7b5cff) ring-2 ring-[#7b5cff] bg-[#232c3a]"
            : "border var(--if-border-color-focus, #0078d4) ring-2 ring-[#c7e0f4] bg-[#f3f9fd]"),
          hasError && (theme === "dark"
            ? "border var(--if-border-color-error, #ff7675) bg-[#2a232a] ring-2 ring-[#ffb6b6]"
            : "border var(--if-border-color-error, #d13438) bg-[#fdf6f6] ring-2 ring-[#f3b6b6]"),
          showSuccess && (theme === "dark"
            ? "border var(--if-border-color-success, #00e676) bg-[#1a2a1a] ring-2 ring-[#00e676]"
            : "border var(--if-border-color-success, #107c10) bg-[#f6fff6] ring-2 ring-[#b7f3b6]"),
          disabled && (theme === "dark"
            ? "bg-[#181c24] text-[#666] cursor-not-allowed"
            : "bg-[#f3f2f1] text-[#a19f9d] cursor-not-allowed"),
          inputClassName
        );

        const inputStyle = clsx(
          "flex-1 min-w-0 bg-transparent border-none outline-none shadow-none p-0 m-0",
          "focus:ring-0 focus:outline-none",
          theme === "dark" ? "placeholder:text-[#b3baff]" : "placeholder:text-[#605e5c]",
          disabled && "bg-transparent",
          "[&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_white]"
        );

        if (as === "textarea") {
          return (
            <div className={wrapperClass}>
              {hasLeftIcon && (
                <span className="icon left-icon mr-2 text-[#605e5c]">{getLeftIcon()}</span>
              )}
              <textarea
                id={inputId}
                name={name || inputId}
                value={getTextareaValue(value)}
                onChange={handleTextareaChange}
                placeholder={effectivePlaceholder} // <-- aqui!
                disabled={disabled}
                readOnly={readOnly}
                className={clsx(
                  "flex-1 min-w-0 bg-transparent border-none outline-none shadow-none p-0 m-0",
                  "focus:ring-0 focus:outline-none",
                  theme === "dark" ? "placeholder:text-[#b3baff]" : "placeholder:text-[#605e5c]",
                  disabled && "bg-transparent",
                  "[&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_white]",
                  inputClassName
                )}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                aria-label={
                  hasError
                    ? resolveMessage(accessibilityErrorMessage || errorMessage, value) || "Invalid input"
                    : resolveMessage(accessibilityLabel, value) || label || effectivePlaceholder || "Input field"
                }
                aria-invalid={hasError}
                aria-required={required}
                aria-describedby={getDescribedById()}
                maxLength={maxLength}
                autoFocus={autoFocus}
                autoComplete={autoComplete ?? "off"}
                tabIndex={typeof tabIndex !== "undefined" ? tabIndex : undefined}
                {...inputProps}
              />
              {showCharCount && typeof value === "string" && maxLength !== undefined && (
                <span className="char-count ml-2 text-xs text-[#605e5c]" aria-live="polite">{`${value.length}/${maxLength}`}</span>
              )}
            </div>
          );
        }

        // Use máscara padrão se não foi passada
        // Troque esta linha:
        // const effectiveMask = mask ?? getDefaultMask(type);
        // por:
        // (já está definido fora, use effectiveMask diretamente)

        // Só usa InputMask se houver máscara (string, array ou RegExp)
        if (
          effectiveMask &&
          (
            typeof effectiveMask === "string" ||
            Object.prototype.toString.call(effectiveMask) === "[object RegExp]" ||
            (Array.isArray(effectiveMask) && effectiveMask.length > 0)
          )
        ) {
          // --- INÍCIO: Use ReplacementSchema para validar replacement ---
          let validReplacement: Record<string, RegExp>;
          if (replacement && typeof replacement === "object") {
            const parsed = ReplacementSchema.safeParse(replacement);
            validReplacement = parsed.success
              ? replacement
              : { "9": /\d/, a: /[A-Za-z]/, "*": /[A-Za-z0-9]/ };
          } else {
            validReplacement = { "9": /\d/, a: /[A-Za-z]/, "*": /[A-Za-z0-9]/ };
          }
          // --- FIM ---

          return (
            <div className={wrapperClass}>
              {hasLeftIcon && (
                <span className="icon left-icon mr-2 text-[#605e5c]">{getLeftIcon()}</span>
              )}
              <InputMask
                {...(maskProps as Partial<CustomInputMaskProps>)}
                mask={effectiveMask}
                id={inputId}
                name={name || inputId}
                value={
                  typeof value === "string" || typeof value === "number"
                    ? value
                    : Array.isArray(value)
                    ? value[0] ?? ""
                    : value != null
                    ? String(value)
                    : ""
                }
                onChange={handleInputChange}
                replacement={validReplacement}
                autoFocus={autoFocus}
                readOnly={readOnly}
                className={clsx(inputStyle, inputClassName)}
                autoComplete={autoComplete ?? "on"}
                placeholder={effectivePlaceholder}
                inputMode={
                  typeof effectiveInputMode === "string" &&
                  [
                    "text",
                    "email",
                    "search",
                    "tel",
                    "none",
                    "url",
                    "numeric",
                    "decimal",
                  ].includes(effectiveInputMode)
                    ? (effectiveInputMode as
                        | "text"
                        | "email"
                        | "search"
                        | "tel"
                        | "none"
                        | "url"
                        | "numeric"
                        | "decimal")
                    : undefined
                }
                {...Object.fromEntries(Object.entries(inputProps || {}).filter(([k]) => k !== "data-testid"))}
              />
            </div>
          );
        }

        const isFileInput = props.type === "file";
        if (isFileInput) {
          return (
            <div className={wrapperClass}>
              {hasLeftIcon && (
                <span className="icon left-icon mr-2 text-[#605e5c]">{getLeftIcon()}</span>
              )}
              <input
                id={inputId}
                name={name || inputId}
                type="file"
                onChange={handleInputChange}
                disabled={disabled}
                readOnly={readOnly}
                className={clsx(inputStyle, inputClassName)}
                ref={inputRef as React.RefObject<HTMLInputElement>}
                aria-label={
                  hasError
                    ? resolveMessage(accessibilityErrorMessage || errorMessage, value) || "Invalid input"
                    : accessibilityLabel || label || placeholder || "Input field"
                }
                aria-invalid={hasError}
                aria-required={required}
                aria-describedby={getDescribedById()}
                multiple={props.multiple}
                accept={props.accept}
                autoFocus={autoFocus}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                {...inputProps}
              />
            </div>
          );
        }

        return (
          <div className={wrapperClass}>
            {hasLeftIcon && (
              <span className="icon left-icon mr-2 text-[#605e5c]">{getLeftIcon()}</span>
            )}
            {/* Input vem antes do label no DOM */}
            <input
              id={inputId}
              name={name || inputId}
              value={getInputValue()}
              onChange={handleInputChange}
              placeholder={effectivePlaceholder}
              disabled={disabled}
              readOnly={readOnly}
              className={clsx(inputStyle, inputClassName)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type={getHtmlInputType()}
              aria-label={
                hasError
                  ? resolveMessage(accessibilityErrorMessage || errorMessage, value) || "Invalid input"
                  : resolveMessage(accessibilityLabel, value) || label || effectivePlaceholder || "Input field"
              }
              aria-invalid={hasError}
              aria-required={required}
              aria-describedby={getDescribedById()}
              autoComplete={autoComplete ?? "on"}
              autoFocus={autoFocus}
              maxLength={maxLength}
              tabIndex={typeof tabIndex !== "undefined" ? tabIndex : undefined}
              inputMode={
                typeof effectiveInputMode === "string" &&
                [
                  "text",
                  "email",
                  "search",
                  "tel",
                  "none",
                  "url",
                  "numeric",
                  "decimal",
                ].includes(effectiveInputMode)
                  ? (effectiveInputMode as
                      | "text"
                      | "email"
                      | "search"
                      | "tel"
                      | "none"
                      | "url"
                      | "numeric"
                      | "decimal")
                  : undefined
              }
              {...Object.fromEntries(Object.entries(inputProps || {}).filter(([k]) => k !== "data-testid"))}
            />
            
            {type === "password" && (
              <span
                className="icon right-icon ml-2 text-[#605e5c] cursor-pointer"
                onClick={handlePasswordIconClick}
                onKeyDown={handlePasswordIconKeyDown}
                role="button"
                aria-label={isPasswordVisible ? "Ocultar senha" : "Mostrar senha"}
                tabIndex={0}
              >
                {isPasswordVisible ? <FaEyeSlash /> : <FaEye />}
              </span>
            )}
            {rightIcon && (
              <span
                className="icon right-icon ml-2 text-[#605e5c] cursor-pointer"
                onClick={onRightIconClick}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (onRightIconClick) onRightIconClick();
                  }
                }}
                role="button"
                aria-label="Right icon action"
                tabIndex={0}
                data-testid="right-icon-key"
              >
                {rightIcon}
              </span>
            )}
          </div>
        );
      };

      const SuccessMessage: React.FC<{
        icon?: React.ReactNode;
        message?: string | ((context: InputFieldMessageContext) => string);
        value: unknown;
        context: InputFieldMessageContext;
      }> = ({ icon, message, context }) => (
        <p className="success-text" id="success-message" aria-live="polite" role="status">
          {icon !== undefined ? (
            <span>{icon}</span>
          ) : (
            <span data-testid="success-icon">
              <FaCheckCircle />
            </span>
          )}
          {/* PATCH: Defensive render for successMessage */}
          {safeRenderMessage(message, context)}
        </p>
      );

      // 3. Defina as custom properties dinamicamente:
      const customStyleVars = {
        ...(borderColor ? { '--if-border-color': borderColor } : {}),
        ...(borderColorFocus ? { '--if-border-color-focus': borderColorFocus } : {}),
        ...(borderColorError ? { '--if-border-color-error': borderColorError } : {}),
        ...(borderColorSuccess ? { '--if-border-color-success': borderColorSuccess } : {}),
        ...(containerProps?.style || {}),
      };

      return (
        <div
          {...containerProps}
          style={customStyleVars}
          role={containerProps?.role ?? "group"}
          data-testid={
            typeof (containerProps as Record<string, unknown>)?.["data-testid"] === "string"
              ? (containerProps as Record<string, unknown>)["data-testid"]
              : (inputProps && typeof inputProps === "object" && "data-testid" in inputProps
                  ? (inputProps as {["data-testid"]?: string})["data-testid"]
                  : "input-field")
          }
          className={clsx(
            "w-full max-w-full relative",
            className,
            containerProps?.className // <-- Adicione esta linha!
          )}
          // REMOVA ESTE BLOCO:
          // tabIndex={
          //   typeof containerProps?.tabIndex !== "undefined"
          //     ? containerProps.tabIndex
          //     : typeof tabIndex !== "undefined"
          //       ? tabIndex
          //       : undefined
          // }
        >
          {(label || tooltip) && (
            <div className={clsx(
              "relative mb-1 h-7 flex items-center gap-1",
              theme === "dark" ? "text-[#bbb]" : "text-[#605e5c]"
            )}>
              {label && (
                <label
                  className={clsx(
                    "font-semibold text-[1rem] leading-tight tracking-tight",
                    "font-inter"
                  )}
                  htmlFor={inputId}
                >
                  {label}
                  {required && (
                    <>
                      <span className="text-[#d13438] ml-1" aria-hidden="true">*</span>
                      <span className="sr-only"> obrigatório</span>
                    </>
                  )}
                </label>
              )}
              {tooltip && (
                <div className="absolute top-0 right-0 flex items-center">
                  <Tooltip
                    tooltip={tooltip}
                    placement={tooltipPlacement}
                    width={tooltipWidth}
                    iconColor={tooltipIconColor}
                    iconSize={tooltipIconSize}
                    tabIndex={0}
                    ariaLive="off"
                    data-testid="inputfield-tooltip"
                  />
                  {/* Texto alternativo visualmente escondido para leitores de tela */}
                  <small className="sr-only" aria-live="polite" aria-atomic="true">
                    {tooltip}
                  </small>
                </div>
              )}
            </div>
          )}

          {/* Use renderInput aqui */}
          {/* PATCH: In renderInput, for textarea, use getTextareaValue */}
          {as === "textarea"
            ? (
              <div className={clsx(
                "relative flex items-center w-full px-3 py-2 rounded-md transition-colors duration-150",
                theme === "dark"
                  ? "bg-[#23242a] text-[#e3eaff] border var(--if-border-color, #33384d) focus-within:border var(--if-border-color-focus, #7b5cff) focus-within:ring-2 focus-within:ring-[#7b5cff] focus-within:bg-[#232c3a]"
                  : "bg-white text-gray-900 border var(--if-border-color, #c8c6c4) focus-within:border var(--if-border-color-focus, #0078d4) focus-within:ring-2 focus-within:ring-[#c7e0f4] focus-within:bg-[#f3f9fd]",
                "font-spaceGrotesk",
                isFocused && !hasError && !showSuccess && (theme === "dark"
                  ? "border var(--if-border-color-focus, #7b5cff) ring-2 ring-[#7b5cff] bg-[#232c3a]"
                  : "border var(--if-border-color-focus, #0078d4) ring-2 ring-[#c7e0f4] bg-[#f3f9fd]"),
                hasError && (theme === "dark"
                  ? "border var(--if-border-color-error, #ff7675) bg-[#2a232a] ring-2 ring-[#ffb6b6]"
                  : "border var(--if-border-color-error, #d13438) bg-[#fdf6f6] ring-2 ring-[#f3b6b6]"),
                showSuccess && (theme === "dark"
                  ? "border var(--if-border-color-success, #00e676) bg-[#1a2a1a] ring-2 ring-[#00e676]"
                  : "border var(--if-border-color-success, #107c10) bg-[#f6fff6] ring-2 ring-[#b7f3b6]"),
                disabled && (theme === "dark"
                  ? "bg-[#181c24] text-[#666] cursor-not-allowed"
                  : "bg-[#f3f2f1] text-[#a19f9d] cursor-not-allowed"),
                inputClassName
              )}>
                {!!getLeftIcon() && (
                  <span className="icon left-icon mr-2 text-[#605e5c]">{getLeftIcon()}</span>
                )}
                <textarea
                  id={inputId}
                  name={name || inputId}
                  value={getTextareaValue(value)}
                  onChange={handleTextareaChange}
                  placeholder={effectivePlaceholder} // <-- aqui!
                  disabled={disabled}
                  readOnly={readOnly}
                  className={clsx(
                    "flex-1 min-w-0 bg-transparent border-none outline-none shadow-none p-0 m-0",
                    "focus:ring-0 focus:outline-none",
                    theme === "dark" ? "placeholder:text-[#b3baff]" : "placeholder:text-[#605e5c]",
                    disabled && "bg-transparent",
                    "[&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_white]",
                    inputClassName
                  )}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                  aria-label={
                    hasError
                      ? resolveMessage(accessibilityErrorMessage || errorMessage, value) || "Invalid input"
                      : resolveMessage(accessibilityLabel, value) || label || effectivePlaceholder || "Input field"
                  }
                  aria-invalid={hasError}
                  aria-required={required}
                  aria-describedby={getDescribedById()}
                  maxLength={maxLength}
                  autoFocus={autoFocus}
                  autoComplete={autoComplete ?? "off"}
                  tabIndex={typeof tabIndex !== "undefined" ? tabIndex : undefined}
                  {...inputProps}
                />
                {showCharCount && typeof value === "string" && maxLength !== undefined && (
                  <span className="char-count ml-2 text-xs text-[#605e5c]" aria-live="polite">{`${value.length}/${maxLength}`}</span>
                )}
              </div>
            )
            : renderInput()
          }

          {as !== "custom" && children}

          {helperText && !(error || hasError) && !showSuccess && (
            <p
              className={clsx(
                "text-sm mt-1 pl-1",
                "font-spaceGrotesk",
                theme === "dark" ? "text-[#d1d5db]" : "text-[#605e5c]"
              )}
              id="helper-text"
              role="note"
              data-testid="helper-text"
            >
              {helperText}
            </p>
          )}
          {/* Renderize SEMPRE o parágrafo de erro, mas só mostre texto se houver erro */}
          {/* Define showErrorText to always show error if error or hasError is true */}
          {(() => {
            // PATCH: Só mostra texto de erro após blur ou erro explícito
            const showErrorText =
              (error || hasError) &&
              (wasBlurred || (zodError && zodError.issues[0]?.message !== "Campo obrigatório"));
            let errorText: string | undefined = "";
            if (showErrorText) {
              // PATCH: Defensive: nunca crasha se errorMessage lança
              errorText = safeRenderMessage(
                errorMessage,
                { value, label, error: zodError, inputType: type },
                "Formato inválido"
              );
              // PATCH: fallback para getErrorText se errorMessage não retornar nada
              if (!errorText) {
                try {
                  errorText =
                    getErrorText(
                      errorMessage,
                      accessibilityErrorMessage,
                      zodError,
                      type,
                      value
                    ) ?? "Formato inválido";
                } catch {
                  errorText = "Formato inválido";
                }
              }
            }
            return (
              <p
                id="error-message"
                className="text-[#d13438] text-sm mt-1 pl-1 animate-fadeIn font-spaceGrotesk min-h-[1.5em]"
                aria-live="assertive"
                role="alert"
                data-testid="error-message"
              >
                {showErrorText ? errorText : ""}
              </p>
            );
          })()}
          {showSuccess && (props.successMessage || props.successIcon) ? (
            <SuccessMessage
              icon={props.successIcon}
              message={props.successMessage}
              value={value}
              context={{
                value,
                label,
                error: zodError,
                inputType: type,
              }}
            />
          ) : null}
        </div>
      );
    }
  ),
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
);

InputField.displayName = "InputField";
export default InputField;

export type InputFieldRef = HTMLInputElement | HTMLTextAreaElement;

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "number") return false;
  if (typeof value === "object") return Object.keys(value as object).length === 0;
  return false;
}

// PATCH 1: Safe value for textarea (undefined/null/other types → "")
const getTextareaValue = (val: unknown): string => {
  if (typeof val === "string") return val;
  if (val === undefined || val === null) return "";
  // Accept numbers, booleans, arrays, objects as empty string for textarea
  return "";
};

// PATCH 2: Defensive error boundary for errorMessage/successMessage rendering
function safeRenderMessage(
  fnOrValue: string | ((context: InputFieldMessageContext) => string) | undefined,
  context: InputFieldMessageContext,
  fallback = "Formato inválido"
) {
  try {
    if (typeof fnOrValue === "function") return fnOrValue(context);
    if (typeof fnOrValue === "string") return fnOrValue;
    return undefined;
  } catch {
    return fallback;
  }
}

function transformText(
  val: string,
  {
    uppercase,
    lowercase,
    capitalize,
    propercase,
  }: {
    uppercase: boolean | undefined;
    lowercase: boolean | undefined;
    capitalize: boolean | undefined;
    propercase: boolean | undefined;
  }
): string {
  if (typeof val !== "string") return "";

  let result = val;

  if (uppercase) {
    result = result.toUpperCase();
  } else if (lowercase) {
    result = result.toLowerCase();
  } else if (capitalize) {
    // Capitalize only the first character, rest as is
    result = result.charAt(0).toUpperCase() + result.slice(1);
  } else if (propercase) {
    // Capitalize the first letter of each word
    result = result.replace(/\b\w/g, (c) => c.toUpperCase());
  }

  return result;
}

